import * as React from "react";
import { DEFAULT_LOCALE, type Locale, isLocale } from "./locales";

import el from "./messages/el.json";
import en from "./messages/en.json";
import ru from "./messages/ru.json";
import sr from "./messages/sr.json";
import ro from "./messages/ro.json";
import bg from "./messages/bg.json";
import mk from "./messages/mk.json";

const MESSAGES: Record<Locale, any> = { el, en, ru, sr, ro, bg, mk };

type Ctx = {
  locale: Locale;
  setLocale: (l: Locale) => void;
  t: (key: string, vars?: Record<string, any>) => string;
};

const I18nCtx = React.createContext<Ctx | null>(null);

function getFromPathname(pathname: string): Locale {
  const first = pathname.split("/").filter(Boolean)[0];
  if (first && isLocale(first)) return first;
  return DEFAULT_LOCALE;
}

function translate(messages: any, key: string, vars?: Record<string, any>) {
  const parts = key.split(".");
  let cur: any = messages;
  for (const p of parts) cur = cur?.[p];
  let out = typeof cur === "string" ? cur : key;

if (vars) {
  Object.entries(vars).forEach(([k, v]) => {
    const token = `{${k}}`;
    const value = String(v);

    // replace all occurrences (safe for older targets)
    out = out.split(token).join(value);
  });
}

  return out;
}

export function I18nProvider({ children }: { children: React.ReactNode }) {
  const [locale, setLocaleState] = React.useState<Locale>(() =>
    getFromPathname(window.location.pathname)
  );

  // όταν αλλάζει URL (popstate), κράτα sync
  React.useEffect(() => {
    const onPop = () => setLocaleState(getFromPathname(window.location.pathname));
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  const setLocale = (l: Locale) => setLocaleState(l);

  const t = React.useCallback(
    (key: string, vars?: Record<string, any>) => translate(MESSAGES[locale], key, vars),
    [locale]
  );

  const value = React.useMemo(() => ({ locale, setLocale, t }), [locale, t]);

  return <I18nCtx.Provider value={value}>{children}</I18nCtx.Provider>;
}

export function useI18n() {
  const ctx = React.useContext(I18nCtx);
  if (!ctx) throw new Error("useI18n must be used inside I18nProvider");
  return ctx;
}
